import { Program, BN } from '@coral-xyz/anchor'
import { createAssociatedTokenAccountInstruction } from '@solana/spl-token'
import { ComputeBudgetProgram, Keypair, PublicKey, SystemProgram, Transaction } from '@solana/web3.js'
import {
  CHAINLINK_PROGRAM_ID,
  findAssociatedTokenAddress,
  getLinkedListAccounts,
  getUshMintPublicKey,
  getVaultTypeOracleAccountPublicKey,
  redeemVaultInstruction,
  Vault,
} from 'hedge-web3'
import TransactionStep from '../../lib/TransactionStep'
import VaultType from '../accounts/VaultType'
import { HEDGE_PROGRAM_PUBLICKEY } from '../utils/HedgeConstants'

/**
 * Redeem USH against a vault to buy collateral
 *
 * @param program Anchor Program to use
 * @param payerPublicKey User who will sign the transaction
 * @param vaultPublicKey Vault to redeem
 * @param redeemAmount Amount to redeem
 */
export async function redeemVaultTransaction(
  program: Program<Vault>,
  payerPublicKey: PublicKey,
  vaultPublicKey: PublicKey,
  vaultType: VaultType,
  collateralMint: PublicKey,
  redeemAmount: BN
): Promise<TransactionStep> {
  return new TransactionStep('Redeem Vault', async () => {
    const { blockhash } = await program.provider.connection.getRecentBlockhash()
    const transaction = new Transaction({
      feePayer: payerPublicKey,
      recentBlockhash: blockhash,
    })
    const signers = []
    const ushMintPublickey = await getUshMintPublicKey(HEDGE_PROGRAM_PUBLICKEY)

    const payerUshAccount = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      payerPublicKey,
      ushMintPublickey
    )

    const vaultTypeAssociatedTokenAccount = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      vaultType.publicKey,
      collateralMint
    )

    const vaultAssociatedTokenAccount = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      vaultPublicKey,
      collateralMint
    )

    const payerCollateralATAAccount = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      payerPublicKey,
      collateralMint
    )

    const vaultTypeOracleAccountPublicKey = await getVaultTypeOracleAccountPublicKey(
      HEDGE_PROGRAM_PUBLICKEY,
      vaultType.name
    )

    const oracleAccountInfo = await program.account.oracleInfoForCollateralType.fetch(
      vaultTypeOracleAccountPublicKey
    )

    transaction.add(
      ComputeBudgetProgram.setComputeUnitLimit({
        units: 400000
      })
    );

    transaction.add(
      await program.methods
        .refreshOraclePrice(
          new BN(0), // override usd/sol price
          new BN(0) // override override time
        )
        .accounts({
          oracleInfoAccount: vaultTypeOracleAccountPublicKey,
          vaultTypeAccount: vaultType.publicKey,
          oracleChainlink: oracleAccountInfo.oracleChainlink,
          oraclePyth: oracleAccountInfo.oraclePyth,
          oracleSwitchboard: oracleAccountInfo.oracleSwitchboard,
          systemProgram: SystemProgram.programId,
          chainlinkProgram: CHAINLINK_PROGRAM_ID,
        })
        .instruction() // or .rpc() or .simulate() etc.
    )

    // Check if we need to create payer associated account for the collateral.
    if (!(await program.provider.connection.getAccountInfo(payerCollateralATAAccount))) {
      console.log('No user collateral associated account found. Adding instruction to create it.')
      transaction.add(
        createAssociatedTokenAccountInstruction(
          payerPublicKey,
          payerCollateralATAAccount,
          payerPublicKey,
          collateralMint
        )
      )
    }

    const [oldSmallerPublicKey, newSmallerPublicKey, newLargerPublicKey] =
      await getLinkedListAccounts(
        program,
        vaultType.publicKey,
        vaultPublicKey,
        new BN(0),
        new BN(0),
        new BN(0),
        new BN(0),
        true,
        false
      )
    const history = Keypair.generate()
    transaction.add(
      await redeemVaultInstruction(
        program,
        payerPublicKey,
        payerUshAccount,
        payerCollateralATAAccount,
        vaultPublicKey,
        vaultAssociatedTokenAccount,
        history.publicKey,
        vaultType.publicKey,
        vaultTypeAssociatedTokenAccount,
        oldSmallerPublicKey,
        newSmallerPublicKey,
        newLargerPublicKey,
        redeemAmount
      )
    )

    transaction.partialSign(history)
    return transaction
  })
}
